import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService, User } from '@auth0/auth0-angular';
import { CompanyContextFieldsFragment } from '@graphql/generated/graphql';
import { NgxRolesService, ValidationFn } from 'ngx-permissions';
import { filter, firstValueFrom } from 'rxjs';
import { CompanyContextService } from './company-context.service';
import { Menu, MenuService } from './menu.service';

export const MENU: Menu[] = [
  {
    route: 'dashboard',
    name: 'dashboard',
    type: 'link',
    icon: 'dashboard',
  },
  {
    route: 'news',
    name: 'news',
    type: 'link',
    icon: 'newspaper',
    permissions: {
      only: ['studio-app'],
    },
  },
  {
    route: 'rewards',
    name: 'rewards',
    type: 'link',
    icon: 'emoji_events',
  },
  {
    route: 'coupons',
    name: 'coupons',
    type: 'sub',
    icon: 'redeem',
    children: [
      {
        route: 'welcome-coupons',
        name: 'welcomeCoupons',
        icon: '',
        type: 'link',
      },
      {
        route: 'coupons',
        name: 'coupons',
        type: 'link',
        icon: '',
      },
    ],
  },
  {
    route: 'challenges',
    name: 'challenges',
    type: 'link',
    icon: 'sports_score',
  },
  {
    route: 'point-system',
    name: 'pointSystem',
    type: 'link',
    icon: 'poll',
  },
  {
    route: 'branch-management',
    name: 'branchManagement',
    type: 'sub',
    icon: 'store',
    children: [
      {
        route: 'branches',
        name: 'branches',
        type: 'link',
        icon: 'store',
      },
      {
        route: 'ratings',
        name: 'ratings',
        type: 'link',
        icon: 'star',
      },
    ],
  },
  {
    route: 'marketing',
    name: 'marketing',
    type: 'sub',
    icon: 'auto_graph',
    children: [
      {
        route: 'push-notification',
        name: 'pushNotification',
        icon: '',
        type: 'link',
      },
      {
        route: 'promotions',
        name: 'promotions',
        type: 'link',
        icon: '',
      },
      {
        route: 'popup',
        name: 'popup',
        type: 'link',
        icon: '',
      },
      {
        route: 'company-referral',
        name: 'companyReferral',
        type: 'link',
        icon: '',
      },
    ],
  },
  {
    route: 'customers',
    name: 'customers',
    type: 'link',
    icon: 'person',
  },
  {
    route: 'equipment-management',
    name: 'equipmentManagement',
    type: 'sub',
    icon: 'fitness_center',
    children: [
      {
        route: 'equipment',
        name: 'equipment',
        type: 'link',
        icon: 'fitness_center',
      },
      {
        route: 'defect-reports',
        name: 'defectReports',
        type: 'link',
        icon: 'assignment_late',
      },
    ],
  },
  {
    route: 'settings',
    name: 'settings',
    type: 'sidenavSub',
    icon: 'settings',
    children: [
      {
        name: 'account',
        type: 'link',
        icon: 'person',
        route: 'account',
        description: 'menu.settings.account.description',
      },
      {
        name: 'company',
        type: 'link',
        icon: 'business',
        route: 'company',
        description: 'menu.settings.company.description',
      },

      {
        name: 'team',
        type: 'link',
        icon: 'groups',
        route: 'team',
        description: 'menu.settings.team.description',
        permissions: {
          only: ['business-owner-role'],
        },
      },
    ],
  },
  {
    name: 'helpCenter',
    route: 'help-center',
    type: 'sub',
    icon: 'help_outline',
    children: [
      {
        name: 'guides',
        route: 'guides',
        icon: '',
        type: 'link',
      },
      {
        name: 'support',
        route: 'support',
        icon: '',
        type: 'link',
      },
    ],
  },
];

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  private readonly _rolesKey = 'userRoles';

  constructor(
    private readonly _companyCtx: CompanyContextService,
    private readonly _auth: AuthService,
    private readonly _menu: MenuService,
    private readonly _roles: NgxRolesService,
    private readonly _title: Title
  ) {}

  async load(): Promise<void> {
    try {
      const [user, company] = await Promise.all([
        firstValueFrom(this._auth.user$),
        firstValueFrom(
          this._companyCtx.company$.pipe(
            filter((c): c is CompanyContextFieldsFragment => c !== null)
          )
        ),
      ]);

      this._setPermissions(user, company);
      this._setMenu(MENU);
      this._setTitle(user);
    } catch (error) {
      throw new Error('Error during startup');
    }
  }

  private _setMenu(menu: Menu[]): void {
    this._menu.addNamespace(menu, 'menu');
    this._menu.set(menu);
  }

  private _setPermissions(
    user: User | null | undefined,
    company: CompanyContextFieldsFragment
  ): void {
    const roles = (user?.[this._rolesKey] as string[]) ?? [];

    const rolesObj: { [name: string]: ValidationFn | string[] } =
      Object.fromEntries(roles.map(role => [role, () => true]));

    if (company?.studioApp) {
      rolesObj['studio-app'] = () => true;
    }

    this._roles.addRoles(rolesObj);
  }

  private _setTitle(user: User | null | undefined): void {
    if (user?.['org_display_name']) {
      this._title.setTitle(`${user['org_display_name']} - GymDeals`);
    }
  }
}
